import * as Sentry from "@sentry/react";
import { getErrorCorrelationId } from "@conciergeco/observability-correlation";

type WebAppSentryFeature =
  | "accounts"
  | "auth"
  | "bookings"
  | "catalog"
  | "dashboard"
  | "inventory"
  | "onboarding"
  | "realtime"
  | "rpc"
  | "sentry"
  | "settings";
type WebAppSentryLevel = "debug" | "error" | "fatal" | "info" | "warning";
type WebAppSentryValue =
  | null
  | boolean
  | number
  | string
  | WebAppSentryValue[]
  | { [key: string]: WebAppSentryValue };
type WebAppSentryData = Record<string, unknown>;

type WebAppSentryOptions = {
  context?: WebAppSentryData;
  feature: WebAppSentryFeature;
  level?: WebAppSentryLevel;
  operation: string;
  tags?: WebAppSentryData;
};

type WebAppBreadcrumbOptions = WebAppSentryOptions & {
  message?: string;
};
type WebAppRpcErrorOptions = {
  path: readonly string[];
  runtime: "browser" | "server";
  transport: "fetch" | "websocket";
};

const capturedErrorSymbol = Symbol.for("conciergeco.webAppSentryCaptured");
const sentryOperationContextName = "conciergeco.operation";
const maxArrayItems = 10;
const maxObjectDepth = 3;
const maxStringLength = 200;
const sensitiveKeyPattern =
  /address|authorization|city|cookie|country|email|filename|header|jwt|messagebody|messagetext|name|originalfilename|otp|passcode|password|phone|postcode|providerassetkey|secret|slug|session|state|token|uploadurl|url|websocketurl/i;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isSensitiveKey(key: string) {
  const normalized = key.replace(/[-_\s]/g, "").toLowerCase();
  return (
    sensitiveKeyPattern.test(normalized) ||
    normalized === "assetrpcpayload" ||
    normalized === "addressline1" ||
    normalized === "addressline2" ||
    normalized === "body" ||
    normalized === "displayname" ||
    normalized === "editordocumentjson" ||
    normalized === "file" ||
    normalized === "fileobject" ||
    normalized === "headers" ||
    normalized === "html" ||
    normalized === "keywords" ||
    normalized === "message" ||
    normalized === "organizationname" ||
    normalized === "plaintext" ||
    normalized === "rawjson" ||
    normalized === "session" ||
    normalized === "text" ||
    normalized === "uploadfile" ||
    normalized === "uploadfileobject"
  );
}

function isUnsafeStringValue(value: string) {
  const normalized = value.trim().toLowerCase();
  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("wss://") ||
    normalized.startsWith("ws://") ||
    normalized.startsWith("blob:") ||
    normalized.includes("bearer ") ||
    normalized.includes("authorization:")
  );
}

function sanitizeString(value: string) {
  if (isUnsafeStringValue(value)) {
    return undefined;
  }

  return value.length > maxStringLength
    ? `${value.slice(0, maxStringLength)}...`
    : value;
}

function sanitizeSentryValue(
  value: unknown,
  depth = 0,
): WebAppSentryValue | undefined {
  if (value === null || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string") {
    return sanitizeString(value);
  }

  if (Array.isArray(value)) {
    const items = value
      .slice(0, maxArrayItems)
      .map((item) => sanitizeSentryValue(item, depth + 1))
      .filter((item): item is WebAppSentryValue => item !== undefined);

    return items;
  }

  if (!isObject(value) || depth >= maxObjectDepth) {
    return undefined;
  }

  const output: Record<string, WebAppSentryValue> = {};

  Object.entries(value).forEach(([key, entryValue]) => {
    if (isSensitiveKey(key)) {
      return;
    }

    const sanitized = sanitizeSentryValue(entryValue, depth + 1);

    if (sanitized !== undefined) {
      output[key] = sanitized;
    }
  });

  return output;
}

function sanitizeSentryData(data: WebAppSentryData | undefined) {
  if (!data) {
    return {};
  }

  const sanitized = sanitizeSentryValue(data);
  return isObject(sanitized) ? sanitized : {};
}

function getErrorProperty(error: unknown, key: string) {
  if (!isObject(error)) {
    return undefined;
  }

  return error[key];
}

function getRpcErrorDetails(error: unknown) {
  const code = getErrorProperty(error, "code");
  const defined = getErrorProperty(error, "defined");
  const status = getErrorProperty(error, "status");

  return {
    errorCode: typeof code === "string" ? code : null,
    errorDefined: typeof defined === "boolean" ? defined : null,
    errorStatus: typeof status === "number" ? status : null,
  };
}

function isAbortLikeError(error: unknown) {
  const code = getErrorProperty(error, "code");
  const name = getErrorProperty(error, "name");

  return (
    code === "aborted" ||
    code === "CLIENT_CLOSED_REQUEST" ||
    name === "AbortError"
  );
}

function wasCapturedByWebAppSentry(
  error: unknown,
  visited = new Set<unknown>(),
): boolean {
  if (!isObject(error) || visited.has(error)) {
    return false;
  }

  visited.add(error);

  if ((error as Record<PropertyKey, unknown>)[capturedErrorSymbol] === true) {
    return true;
  }

  return wasCapturedByWebAppSentry(error.cause, visited);
}

function markCapturedByWebAppSentry(error: unknown) {
  if (!isObject(error)) {
    return;
  }

  Object.defineProperty(error, capturedErrorSymbol, {
    configurable: true,
    enumerable: false,
    value: true,
  });
}

function applyOperationScope(
  scope: Sentry.Scope,
  options: WebAppSentryOptions,
) {
  const tags = sanitizeSentryData(options.tags);
  const context = sanitizeSentryData(options.context);

  scope.setTag("feature", options.feature);
  scope.setTag("operation", options.operation);

  Object.entries(tags).forEach(([key, value]) => {
    if (
      typeof value === "boolean" ||
      typeof value === "number" ||
      typeof value === "string"
    ) {
      scope.setTag(key, String(value));
    }
  });

  if (options.level) {
    scope.setLevel(options.level);
  }

  scope.setContext(sentryOperationContextName, {
    feature: options.feature,
    operation: options.operation,
    ...context,
  });
}

function captureWebAppException(error: unknown, options: WebAppSentryOptions) {
  if (isAbortLikeError(error) || wasCapturedByWebAppSentry(error)) {
    return;
  }

  Sentry.withScope((scope) => {
    applyOperationScope(scope, options);
    Sentry.captureException(error);
  });
  markCapturedByWebAppSentry(error);
}

function captureWebAppMessage(message: string, options: WebAppSentryOptions) {
  Sentry.withScope((scope) => {
    applyOperationScope(scope, options);
    Sentry.captureMessage(message);
  });
}

function addWebAppBreadcrumb(options: WebAppBreadcrumbOptions) {
  Sentry.addBreadcrumb({
    category: `web-app.${options.feature}`,
    data: sanitizeSentryData(options.context),
    level: options.level ?? "info",
    message: options.message ?? options.operation,
    type: "default",
  });
}

function captureWebAppRpcError(error: unknown, options: WebAppRpcErrorOptions) {
  const errorDetails = getRpcErrorDetails(error);
  const correlationId = getErrorCorrelationId(error);
  const procedure = options.path.join(".");
  const context = {
    ...errorDetails,
    correlationId,
    pathDepth: options.path.length,
    procedure,
    runtime: options.runtime,
    transport: options.transport,
  };

  if (
    errorDetails.errorStatus !== null &&
    errorDetails.errorStatus >= 400 &&
    errorDetails.errorStatus < 500
  ) {
    addWebAppBreadcrumb({
      context,
      feature: "rpc",
      level: "warning",
      operation: "rpc.defined_error",
      tags: {
        correlation_id: correlationId,
        runtime: options.runtime,
        transport: options.transport,
      },
    });
    markCapturedByWebAppSentry(error);
    return;
  }

  captureWebAppException(error, {
    context,
    feature: "rpc",
    level: "error",
    operation: "rpc.call",
    tags: {
      correlation_id: correlationId,
      runtime: options.runtime,
      transport: options.transport,
    },
  });
}

export {
  addWebAppBreadcrumb,
  captureWebAppException,
  captureWebAppMessage,
  captureWebAppRpcError,
  sanitizeSentryData,
  sentryOperationContextName,
  wasCapturedByWebAppSentry,
  type WebAppBreadcrumbOptions,
  type WebAppRpcErrorOptions,
  type WebAppSentryData,
  type WebAppSentryFeature,
  type WebAppSentryLevel,
  type WebAppSentryOptions,
};
